import type { JSX } from 'react';

import { GridColDef, GridRowId, GridRowModel, GridRowParams, GridSortModel } from '@mui/x-data-grid';
import { IUserProfileTenant } from 'features/userProfileApps/UserProfileApps';

export interface IEditingItemInfo<T> {
    editingItemId?: GridRowId;
    editingRow?: T;
}

export type ActionDefinition = {
    action: Operations,
    disabled: boolean | ((row: GridRowModel) => boolean),
    urlToOpen?: ((id: GridRowId, row?: GridRowModel) => string),
    visible?: ((row: GridRowModel) => boolean) 
}

export type GridActionsColDef =  GridColDef & {
    getActions: (params: GridRowParams) => JSX.Element[];
}

export type EditDrawerContentProps<T = unknown> = {
    title: string;
    editingItemInfo?: IEditingItemInfo<T>;
    tenant?: IUserProfileTenant;
    rowCount?: number;
};

export type DeleteDialogContentProps<T = unknown> = {
    title?: string;
    itemId?: GridRowId;
    editingRow?: T;
    customDeleteTitle?: string;
    tenant?: IUserProfileTenant;
    paginationInfo?: {
        rowsOnPage?: number;
    };
    updatePageCallback?: () => void;
};

export type ExportDialogContentProps = {
    cancelExportCallback: () => void;
};

export type SuccessCreateContentProps = {
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    open: boolean;
    createResult: string | undefined;
};

export enum Operations {
    Edit = 'Edit',
    Create = 'Create',
    Open = 'Open',
    OpenMembership = 'Open Membership',
    OpenProfile = 'Open Profile',
    Delete = 'Delete',
    Link = 'Link',
    OpenLicense = 'Open License',
    OpenUserGroup = 'Open User Group'
}

export type ApplicationSuiteDetailStateProps = {
    applicationSuiteName: string;
    applicationSuiteId: string;
};

export type SuccessApiKeyCreationPopupProps = {
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    open: boolean;
    apiKeyName: string;
    apiKeyValue: string;
};

export enum ColumnType {
    First = 'First',
    Tooltip = 'Tooltip',
    Chip = 'Chip',
    Chips = 'Chips',
    Action = 'Action',
    DatetimeTooltip = 'DatetimeTooltip'
}

export type ColumnDefinition = {
    columnName: string;
    columnId: string;
    columnType: ColumnType;
    copy?: boolean;
    size?: number;
    headerSvg?: boolean;
    sortable?: boolean;
    withCountBubble?: boolean;
    headerPrompt?: string;
    align?: 'left' | 'right';
    toolTip?: (row?: GridRowModel) => JSX.Element;
    shouldFilterByExactMatch?: boolean;
}

export enum DrawerEntitiesNames {
    AppSuiteUserGroup = 'appSuiteUserGroup',
    UserProfileUserGroup = 'userProfileUserGroup',
    UserForeignSystems = 'userForeignSystems',
    UserIdentities = 'userIdentities',
    UserTenant = 'userTenant',
    UserProfileRolesMembership = 'userProfileRolesMembership',
    AppSuiteUserRoleMembership = 'appSuiteUserRoleMembership',
    UserAcls = 'userAcls',
    UserGlobalRoles='userGlobalRoles',
    UserProfileClaims = 'userProfileClaims',
    userProfileLicenses = 'userProfileLicenses',
    UserProfile = 'userProifle'
}

export type FetchExportDataFunction =
    (signal: AbortSignal, search?: string, model?: GridSortModel) =>
         Promise<GridRowModel[]>;

export type FetchExportDataResult = {
    data?: GridRowModel[],
    isSuccessful?: boolean,
    isClientSidePagination: boolean
};

//UserAclEntries/UserProfileAcls/UserGroupAcls tooltip typeDefs:
export type AclEntriesStatusTooltipProps = {
    UserStartDate?: string;
    UserEndDate?: string;
    LicenseStartDate?: string;
    LicenseEndDate?: string;
    AclStartDate?: string;
    AclEndDate?: string;
    UserInactive?: boolean;
    LicenseInactive: boolean;
    AclInactive: boolean;
}

export type AclEntriesInactiveStatuses = 'UserInactive' | 'LicenseInactive' | 'AclInactive';
export type AclEntriesStatusTooltipDates = Omit<AclEntriesStatusTooltipProps, AclEntriesInactiveStatuses>;

export type AclEntriesStatusTooltipDateKeys = Exclude<keyof AclEntriesStatusTooltipDates, AclEntriesInactiveStatuses>;

export type AclEntriesTooltipBaseEntitiesConfig = {
    entityName: string,
    title: string,
    testId: string,
    inactiveTestId: string,
    inactiveReasonMessage: string
};

export type AclEntriesStatusTooltipContent = {
    message: string,
    testId: string,
    inactiveTestId?: string
    inactive?: boolean,
    inactiveReasonMessage?: string
};

export type DropDownInfo = {
    dropdownInfo: {
        id: string;
        label: string;
        searchOptions: string[];
        checkBoxes: {
            label: string;
            id?: string;
        }[];
        isSearch: boolean;
        hasClear: boolean;
    };
    dropdownReducer: (label: string) => void;
}
